import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WeddingProvider, useWedding } from './contexts/WeddingContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import MagicLoginPage from './pages/MagicLoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import EventsPage from './pages/EventsPage';
import ExpensesPage from './pages/ExpensesPage';
import VendorsPage from './pages/VendorsPage';
import GuestsPage from './pages/GuestsPage';
import ChecklistPage from './pages/ChecklistPage';
import SettingsPage from './pages/SettingsPage';
import PaymentsPage from './pages/PaymentsPage';
import BudgetPage from './pages/BudgetPage';
import SeatingPage from './pages/SeatingPage';
import TimelinePage from './pages/TimelinePage';
import CalendarPage from './pages/CalendarPage';
import OnboardingPage from './pages/OnboardingPage';
import PaywallPage from './pages/PaywallPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import AdminPage from './pages/AdminPage';
import './App.css';

// Protected Layout - single WeddingProvider wrapping all protected routes
function ProtectedLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-muted font-light">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Paywall gate: any logged-in user without paid access goes to /paywall.
  // Collaborators piggy-back on the owner's paid access — they bypass the paywall.
  if (!user.is_paid && !user.is_collaborator) {
    return <Navigate to="/paywall" replace />;
  }

  return (
    <WeddingProvider>
      <OnboardingGate>
        <Outlet />
      </OnboardingGate>
    </WeddingProvider>
  );
}

/**
 * If a paid user has zero weddings AND we're not already on /onboarding,
 * redirect them to the onboarding wizard. The wizard is skippable, so this
 * only fires once per fresh account.
 */
function OnboardingGate({ children }) {
  const { weddings, weddingsInitialised } = useWedding();
  const { user } = useAuth();
  const location = useLocation();
  // Collaborators inherit the owner's wedding(s); they already have data so
  // skip the gate for them.
  if (user?.is_collaborator) return children;
  // Wait until the first /api/weddings call resolves — otherwise the
  // initial empty `weddings` array would trigger a wrong redirect.
  if (!weddingsInitialised) return children;
  const hasWedding = (weddings || []).length > 0;
  if (!hasWedding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

// Paywall route - requires auth but NOT paid status
function PaywallRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-muted font-light">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.is_paid || user.is_collaborator) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route wrapper - for /login and /register
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-muted font-light">Loading...</div>
      </div>
    );
  }

  if (user) {
    const hasAccess = user.is_paid || user.is_collaborator;
    return <Navigate to={hasAccess ? "/dashboard" : "/paywall"} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Root → login (PublicRoute redirects logged-in users to /dashboard or /paywall) */}
      <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
      {/* One-click magic sign-in — from Kajabi welcome email, partner invites, etc. */}
      <Route path="/magic-login" element={<MagicLoginPage />} />
      
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Paywall routes (auth required, NOT paid) */}
      <Route path="/paywall" element={<PaywallRoute><PaywallPage /></PaywallRoute>} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      
      {/* Protected routes - single WeddingProvider via layout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        {/* Old standalone URLs kept for back-compat — both redirect into the
            unified Budget page so existing email links + bookmarks still work. */}
        <Route path="/expenses" element={<Navigate to="/budget" replace />} />
        <Route path="/payments" element={<Navigate to="/budget" replace />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/guests" element={<GuestsPage />} />
        <Route path="/checklist" element={<ChecklistPage />} />
        <Route path="/seating" element={<SeatingPage />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Route>
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
